import globalAgent from 'src/agent';
import { URLS } from 'src/constants/urls';

export interface IYandexDirectConnectionResponse {
    status: 'not_connected' | 'connected' | 'expired' | 'revoked' | 'error' | string;
    connection: null | {
        id: number;
        status: string;
        login: string;
        client_login: string;
        is_agency: boolean;
        last_units_used?: number | null;
        last_units_available?: number | null;
        last_units_limit?: number | null;
        last_checked_at?: string | null;
        metadata?: Record<string, any>;
    };
    next_action: 'connect_direct' | 'run_audit' | string;
}

export interface IYandexDirectAuditFinding {
    id: number;
    severity: 'P0' | 'P1' | 'P2' | 'P3' | string;
    finding_type: string;
    title: string;
    description: string;
    affected_objects: any[];
    evidence: Record<string, any>;
    recommended_action: string;
    can_auto_fix: boolean;
    auto_fix_payload: Record<string, any>;
    status: string;
}

export interface IYandexDirectAuditRun {
    id: number;
    project: number;
    status: string;
    mode: string;
    overall_risk: 'green' | 'yellow' | 'red' | string;
    can_apply_changes: boolean;
    compliance_summary: Record<string, any>;
    performance_summary: Record<string, any>;
    raw_refs: Record<string, any>;
    error_message: string;
    created_at: string;
    completed_at?: string | null;
    findings: IYandexDirectAuditFinding[];
}

class YandexDirectService {
    async getConnection(): Promise<IYandexDirectConnectionResponse> {
        const response = await globalAgent.get(URLS.YANDEX_DIRECT_CONNECTION);
        return response.data;
    }

    async startOAuth(redirectAfter?: string): Promise<{
        authorization_url: string;
        state: string;
        expires_at: string;
    }> {
        const response = await globalAgent.post(URLS.YANDEX_DIRECT_OAUTH_START, {
            redirect_after: redirectAfter || '',
        });
        return response.data;
    }

    async checkConnection(): Promise<IYandexDirectConnectionResponse> {
        const response = await globalAgent.post(URLS.YANDEX_DIRECT_CONNECTION_CHECK);
        return response.data;
    }

    async confirmAdvertiserData(data: {
        project_id?: number;
        confirmed_flags: Record<string, boolean>;
    }): Promise<IYandexDirectConnectionResponse> {
        const response = await globalAgent.post(URLS.YANDEX_DIRECT_CONFIRM_ADVERTISER_DATA, {
            confirmed_at: new Date().toISOString(),
            ...data,
        });
        return response.data;
    }

    async runAudit(projectId: number, data: Record<string, any> = {}): Promise<IYandexDirectAuditRun> {
        const response = await globalAgent.post(`${URLS.YANDEX_DIRECT_PROJECTS}/${projectId}/audit/`, data);
        return response.data;
    }

    async applyAuditRecommendations(
        projectId: number,
        auditId: number,
        data: {
            finding_ids: number[];
            confirmed_flags: Record<string, boolean>;
        }
    ): Promise<Record<string, any>> {
        const response = await globalAgent.post(
            `${URLS.YANDEX_DIRECT_PROJECTS}/${projectId}/audit/${auditId}/apply/`,
            data
        );
        return response.data;
    }
}

export default new YandexDirectService();
